import React, { useEffect, useState, useMemo, useCallback } from "react";
import Select from "react-select";
import { usePageTitle } from "../../../hooks/useMeta";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faImageSlash,
} from "@fortawesome/pro-solid-svg-icons";
import useApi from "../../../hooks/useApi";
import { getAllCompanies } from "../../../services/admin/companyService";
import Table from "../../table/Table";
import NoData from "../../errors/NoData";
import DecoratedInput from "../../inputs/DecoratedInput";
import DropdownButton from "../../buttons/DropdownButton";
import { formatPhoneNumber } from "../../../utils/formatting";
import { startControllingCompany } from "../../../redux/slices/adminSlice";
import useLangNavigate from "../../../hooks/useLangNavigate";
import Spinner from "../../utils/Spinner";
import Tag from "../../utils/Tag";

const getCompanyStatus = (company) =>
  company.deletedAt ? "closed" : "active";

export const AdminCompanies = ({
  tKey = "dashboard.admin.adminCompanies.",
}) => {
  usePageTitle("adminCompanies");
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [filtered, setFilteredCompanies] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const dispatch = useDispatch();
  const navigate = useLangNavigate();
  const { request: getCompanies, loading } = useApi(getAllCompanies);

  const statusOptions = useMemo(
    () => [
      { value: "active", label: t(`${tKey}status.active`) },
      { value: "closed", label: t(`${tKey}status.closed`) },
    ],
    [t, tKey]
  );

  const handleCompanyControl = useCallback(
    (row) => {
      dispatch(startControllingCompany(row.values));
      navigate("dashboard");
    },
    [dispatch, navigate]
  );

  const handleCompanyDetail = useCallback(
    (row) => {
      navigate(`dashboard/admin-companies/${row.original.companyId}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "companyId",
        Cell: "",
      },
      {
        Header: t(`${tKey}name`),
        accessor: "name",
        Cell: ({ row }) => {
          const status = getCompanyStatus(row.original);
          return (
            <div className="flex align-center gap-1">
              {status !== "active" && (
                <Tag
                  variant="danger"
                  tKey={`${tKey}status.`}
                  text={status}
                />
              )}
              <span>{row.original.name}</span>
            </div>
          );
        },
      },
      {
        Header: t(`${tKey}image`),
        accessor: "imagePath",
        Cell: ({ row }) =>
          row.original.imagePath ? (
            <img
              className="list-preview-img"
              src={row.original.imagePath}
              alt={row.original.name}
              height={50}
            />
          ) : (
            <FontAwesomeIcon icon={faImageSlash} className="text-medium" />
          ),
        disableSortBy: true,
      },
      {
        Header: t(`${tKey}address`),
        accessor: "address",
      },
      {
        Header: t(`${tKey}city`),
        accessor: (row) => (
          <>
            <div>{`${row.city.npa} ${row.city.label}`}</div>
            <div className="text-light">{`${row.region.label}`}</div>
          </>
        ),
      },
      {
        Header: t(`${tKey}contact`),
        accessor: (row) => (
          <>
            <div>
              <a href={`mailto:${row.email}`}>{row.email}</a>
            </div>
            <div>{formatPhoneNumber(row.phone)}</div>
          </>
        ),
      },
      {
        Header: t(`${tKey}employees`),
        accessor: (row) => row.usersCount,
      },
      {
        Header: "",
        id: "actions",
        Cell: ({ row }) => (
          <DropdownButton
            actions={[
              {
                label: t(`${tKey}actions.edit`),
                onClick: () => handleCompanyDetail(row),
              },
              {
                label: t(`${tKey}actions.takeControl`),
                onClick: () => handleCompanyControl(row),
              },
            ]}
          >
            <FontAwesomeIcon color="grey" icon={faEllipsisVertical} />
          </DropdownButton>
        ),
        disableSortBy: true,
      },
    ],
    [handleCompanyControl, handleCompanyDetail, t, tKey]
  );

  useEffect(() => {
    const fetchCompaniesData = async () => {
      await getCompanies()
        .then((response) => {
          setCompanies(response.data);
        })
        .catch((error) => {
          console.log(error);
        });
    };
    fetchCompaniesData();
  }, [getCompanies]);

  useEffect(() => {
    let next = companies;
    if (query) {
      next = next.filter((company) =>
        company.name.toLowerCase().includes(query.toLowerCase())
      );
    }
    if (statusFilter) {
      next = next.filter(
        (company) => getCompanyStatus(company) === statusFilter.value
      );
    }
    setFilteredCompanies(next);
  }, [companies, query, statusFilter]);

  if (loading) return <Spinner />;

  return (
    <div className="dashboard-content">
      <div className="flex gap-2 align-center">
        <DecoratedInput
          className="bg-white pl-2 dashboard-search-bar w-100"
          input={{
            id: "searchCompany",
            class: "p4",
            value: "",
            placeholder: t(`${tKey}searchPlaceholder`, {
              count: companies ? companies.length : 0,
            }),
          }}
          hasResearchButton
          onResearchButtonClick={(searchQuery) => {
            setQuery(searchQuery);
          }}
        />
        <Select
          className="react-custom-select dashboard-select col-3"
          classNamePrefix="react-select"
          isClearable
          placeholder={t(`${tKey}status.label`)}
          options={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
        />
      </div>
      <div className="table-container mt-4">
        {filtered?.length === 0 ? (
          <NoData />
        ) : (
          <Table
            rowClassName="p-4"
            columns={columns}
            data={filtered}
            onClickRow={handleCompanyDetail}
          />
        )}
      </div>
    </div>
  );
};

export default AdminCompanies;
